import { Button, Drawer as AntdDrawer, DrawerProps as AntdDrawerProps, Flex } from "antd";
import { DrawerId as CCDrawerId } from "components/CC/types/models.ts";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { DRAWER_WIDTH } from "localConstants";
import { ReactNode, useEffect, useRef } from "react";
import { DrawerId } from "types/models.ts";
import { clearPendingDrawerVisibility, usePendingDrawerVisibility } from "utils/drawerVisibilityStore";

// Accept both global DrawerId and CC-specific DrawerId
type AnyDrawerId = DrawerId | CCDrawerId;

// Global registry to track rendered drawers and prevent duplicates
const renderedDrawers = new Set<AnyDrawerId>();

export interface DrawerProps extends AntdDrawerProps {
    /** The unique identifier for this drawer, used for URL-based state management */
    drawerId: AnyDrawerId;
    /**
     * Array of search param keys to clean when drawer closes.
     * Use with `getDrawerSearchParams` helper for consistent naming.
     * @example searchParamsToClean={getDrawerSearchParams(DrawerId.EditEntity, 'id', 'type')}
     */
    searchParamsToClean?: string[];
    /**
     * Additional buttons to render before the auto-generated cancel button.
     * Typically used for Save/Submit buttons.
     * @example extraButtons={<Button type="primary" onClick={handleSave}>Save</Button>}
     */
    extraButtons?: ReactNode;
    /**
     * Flag to control cancel button visibility.
     * @default true
     */
    showCancelButton?: boolean;
    /**
     * Optional callback invoked before closing. Return false to prevent close.
     * Useful for unsaved changes confirmation.
     * @example onBeforeClose={() => !isDirty || window.confirm("Discard changes?")}
     */
    onBeforeClose?: () => Promise<boolean> | boolean;
}

/**
 * Base Drawer component with automatic cancel button generation and URL param management.
 *
 * @description
 * This component wraps Ant Design's Drawer with automatic cancel button rendering
 * and URL search param cleanup on close. It supports backward compatibility with
 * the old `extra` prop pattern.
 *
 * @example Simple drawer (automatic cancel button)
 * ```tsx
 * <Drawer drawerId={DrawerId.ViewDetails} title={<DrawerTitle title="Details" />}>
 *   {content}
 * </Drawer>
 * ```
 *
 * @example Drawer with search params cleanup
 * ```tsx
 * <Drawer
 *   drawerId={DrawerId.EditEntity}
 *   searchParamsToClean={getDrawerSearchParams(DrawerId.EditEntity, 'id')}
 *   extraButtons={<Button type="primary" onClick={handleSave}>Save</Button>}
 * >
 *   {content}
 * </Drawer>
 * ```
 *
 * @example Drawer with confirmation before close
 * ```tsx
 * <Drawer
 *   drawerId={DrawerId.EditForm}
 *   onBeforeClose={() => !isDirty || window.confirm("Discard changes?")}
 * >
 *   {content}
 * </Drawer>
 * ```
 *
 * @migration
 * Old pattern (deprecated):
 * ```tsx
 * const handleClose = () => drawerUtils.hide(id, params);
 * <Drawer extra={<Button onClick={handleClose}>Cancel</Button>} />
 * ```
 *
 * New pattern:
 * ```tsx
 * <Drawer searchParamsToClean={params} extraButtons={...} />
 * ```
 */
export const Drawer = ({
    drawerId,
    searchParamsToClean,
    extraButtons,
    showCancelButton = true,
    onBeforeClose,
    ...props
}: DrawerProps) => {
    const drawerUtils = useDrawer();
    const { t: tC } = usePaldockTranslation("common");
    const isRegistered = useRef(false);
    const drawerIdString = drawerId.toString();
    const isOpenFromUrl = drawerUtils.isDrawerActive(drawerId);
    const pendingVisibility = usePendingDrawerVisibility(drawerIdString);
    const isOpen = pendingVisibility ?? isOpenFromUrl;

    // Register this drawer instance and prevent duplicates
    useEffect(() => {
        if (renderedDrawers.has(drawerId)) {
            // Another instance already exists, this one should not render
            return;
        }
        renderedDrawers.add(drawerId);
        isRegistered.current = true;

        return () => {
            if (isRegistered.current) {
                renderedDrawers.delete(drawerId);
            }
        };
    }, [drawerId]);

    useEffect(() => {
        if (pendingVisibility !== undefined && pendingVisibility === isOpenFromUrl) {
            clearPendingDrawerVisibility(drawerIdString);
        }
    }, [drawerIdString, isOpenFromUrl, pendingVisibility]);

    // If another instance of this drawer is already rendered, skip this one
    if (renderedDrawers.has(drawerId) && !isRegistered.current) {
        return null;
    }

    const handleClose = async () => {
        if (onBeforeClose) {
            const shouldClose = await onBeforeClose();
            if (!shouldClose) {
                return;
            }
        }
        drawerUtils.hide(drawerId, searchParamsToClean);
    };

    // Backward compatibility: if props.extra is provided, use old pattern
    // Otherwise, use new headerButtons pattern with automatic cancel button
    const headerButtons = (
        <Flex gap={"small"} align={"center"}>
            {extraButtons}
            {showCancelButton && (
                <Button size={"large"} onClick={handleClose}>
                    {tC("cancel")}
                </Button>
            )}
        </Flex>
    );

    // Use props.extra for backward compatibility if provided, otherwise use new pattern
    const extraContent = props.extra !== undefined ? props.extra : headerButtons;

    return (
        <AntdDrawer
            {...props}
            key={drawerId}
            onClose={handleClose}
            open={isOpen}
            width={props.width ?? DRAWER_WIDTH}
            extra={extraContent}
            mask={true}
            styles={{
                ...props.styles,
                body: {
                    paddingBottom: 80,
                    ...props.styles?.body,
                },
            }}
            closeIcon={showCancelButton ? props.closeIcon : false}
        >
            {props.children}
        </AntdDrawer>
    );
};
